import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/auth/AuthContext'

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3200)
    return () => clearTimeout(t)
  }, [onDone])

  const C = {
    success: { bg: '#E8F5E9', border: '#2E7D32', color: '#2E7D32' },
    error:   { bg: '#FFEBEE', border: '#C62828', color: '#C62828' },
    info:    { bg: '#E3F2FD', border: '#1565C0', color: '#1565C0' },
  }
  const c = C[type] || C.info
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 10,
      padding: '12px 18px', fontSize: 13, fontWeight: 700, color: c.color,
      boxShadow: '0 4px 20px rgba(0,0,0,0.12)', maxWidth: 360,
      animation: 'slideUp .25s ease',
    }}>
      {message}
      <style>{`@keyframes slideUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  )
}

// ─── Parent Row ───────────────────────────────────────────────────────────────
function ParentRow({ parent, childId, authHdr, onRefresh, onToast }) {
  const [resetting,     setResetting]     = useState(false)
  const [revoking,      setRevoking]      = useState(false)
  const [newPwd,        setNewPwd]        = useState('')
  const [showReset,     setShowReset]     = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  async function handleReset() {
    if (newPwd.length < 8) { onToast('Password must be at least 8 characters.', 'error'); return }
    setResetting(true)
    try {
      const r = await fetch(`/api/children/${childId}/parent/${parent._id}/reset`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHdr() },
        body: JSON.stringify({ newPassword: newPwd }),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.message)
      onToast('✅ Password reset successfully.', 'success')
      setNewPwd(''); setShowReset(false)
    } catch (e) {
      onToast(e.message || 'Reset failed.', 'error')
    } finally { setResetting(false) }
  }

  async function handleRevoke() {
    setRevoking(true)
    try {
      const r = await fetch(`/api/children/${childId}/parent/${parent._id}`, {
        method: 'DELETE', headers: authHdr(),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.message)
      onToast('✅ Parent access revoked.', 'success')
      onRefresh()
    } catch (e) {
      onToast(e.message || 'Revoke failed.', 'error')
    } finally { setRevoking(false); setConfirmRevoke(false) }
  }

  function relDate(iso) {
    if (!iso) return 'Never'
    const d = Math.floor((Date.now() - new Date(iso)) / 86400000)
    if (d === 0) return 'Today'
    if (d === 1) return 'Yesterday'
    if (d < 7)  return `${d}d ago`
    return new Date(iso).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })
  }

  return (
    <div style={{
      background: 'white', borderRadius: 12,
      border: `1.5px solid ${parent.isActive !== false ? '#C8E6FA' : '#FFCDD2'}`,
      padding: '14px 16px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: parent.isActive !== false ? '#1A4A6E' : '#9E9E9E',
          color: 'white', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontWeight: 900, fontSize: 14, flexShrink: 0,
        }}>
          {(parent.name || '?').trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: 13, color: '#1A1A2E' }}>{parent.name}</span>
            {parent.isActive === false && (
              <span style={{ fontSize: 9, fontWeight: 800, color: '#C62828', background: '#FFEBEE', padding: '1px 6px', borderRadius: 4 }}>
                DEACTIVATED
              </span>
            )}
            {parent._isNew && (
              <span style={{ fontSize: 9, fontWeight: 800, color: '#1B6B45', background: '#E8F5E9', padding: '1px 6px', borderRadius: 4 }}>
                NEW
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#9E9E9E', marginTop: 1 }}>{parent.email}</div>
          <div style={{ fontSize: 10, color: '#BDBDBD', marginTop: 2 }}>Last login: {relDate(parent.lastLogin)}</div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            onClick={() => { setShowReset(s => !s); setConfirmRevoke(false) }}
            style={{
              padding: '5px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', border: '1.5px solid #1A4A6E',
              background: showReset ? '#1A4A6E' : 'white',
              color: showReset ? 'white' : '#1A4A6E',
            }}
          >
            🔑 Reset PW
          </button>
          <button
            onClick={() => { setConfirmRevoke(s => !s); setShowReset(false) }}
            style={{
              padding: '5px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700,
              cursor: 'pointer', border: '1.5px solid #C62828',
              background: confirmRevoke ? '#C62828' : 'white',
              color: confirmRevoke ? 'white' : '#C62828',
            }}
          >
            🚫 Revoke
          </button>
        </div>
      </div>

      {showReset && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          <input
            type="password" placeholder="New password (min 8 chars)"
            value={newPwd} onChange={e => setNewPwd(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleReset()}
            style={{ flex:1, padding:'8px 12px', borderRadius:7, fontSize:12, border:'1.5px solid #E4EBF2', outline:'none' }}
          />
          <button onClick={handleReset} disabled={resetting}
            style={{ padding:'8px 14px', borderRadius:7, fontSize:12, fontWeight:700, background:'#1A4A6E', color:'white', border:'none', cursor:'pointer', opacity:resetting?0.7:1 }}>
            {resetting ? '…' : 'Save'}
          </button>
          <button onClick={() => { setShowReset(false); setNewPwd('') }}
            style={{ padding:'8px 10px', borderRadius:7, fontSize:12, background:'#F5F5F5', border:'none', cursor:'pointer' }}>
            ✕
          </button>
        </div>
      )}

      {confirmRevoke && (
        <div style={{
          marginTop: 10, background: '#FFF3F3', borderRadius: 8,
          border: '1px solid #FFCDD2', padding: '10px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ fontSize: 11, color: '#C62828', fontWeight: 600 }}>
            Remove {parent.name}'s access permanently?
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={handleRevoke} disabled={revoking}
              style={{ padding:'5px 12px', borderRadius:6, fontSize:11, fontWeight:700, background:'#C62828', color:'white', border:'none', cursor:'pointer', opacity:revoking?0.7:1 }}>
              {revoking ? 'Removing…' : 'Yes, Revoke'}
            </button>
            <button onClick={() => setConfirmRevoke(false)}
              style={{ padding:'5px 10px', borderRadius:6, fontSize:11, background:'white', border:'1px solid #ddd', cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Create Form ──────────────────────────────────────────────────────────────
function CreateParentForm({ childId, childName, authHdr, onCreated, onToast }) {
  const [saving,  setSaving]  = useState(false)
  const [showPwd, setShowPwd] = useState(false)
  const [form,    setForm]    = useState({ name: '', email: '', password: '' })

  function generatePassword() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    let pwd = ''
    for (let i = 0; i < 10; i++) pwd += chars[Math.floor(Math.random() * chars.length)]
    setForm(f => ({ ...f, password: pwd }))
    setShowPwd(true)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) {
      onToast('All fields are required.', 'error'); return
    }
    if (form.password.length < 8) {
      onToast('Password must be at least 8 characters.', 'error'); return
    }
    setSaving(true)
    try {
      const r = await fetch(`/api/children/${childId}/parent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHdr() },
        body: JSON.stringify(form),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.message)
      onToast('✅ Parent account created!', 'success')
      const newParent = d.parent || { ...form, _id: d.parentId || String(Date.now()), _isNew: true, isActive: true }
      onCreated(newParent)
      setForm({ name: '', email: '', password: '' })
    } catch (e) {
      onToast(e.message || 'Failed to create account.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const firstName = childName.split(' ')[0]

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={{ fontSize:11, fontWeight:700, color:'#555', display:'block', marginBottom:5 }}>
          Parent / Guardian Name *
        </label>
        <input type="text" placeholder="e.g. Rajan Pillai" value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={IS} />
      </div>
      <div>
        <label style={{ fontSize:11, fontWeight:700, color:'#555', display:'block', marginBottom:5 }}>
          Email Address *
        </label>
        <input type="email" placeholder="parent@example.com" value={form.email}
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={IS} />
      </div>
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:5 }}>
          <label style={{ fontSize:11, fontWeight:700, color:'#555' }}>Password *</label>
          <button type="button" onClick={generatePassword}
            style={{ fontSize:10, fontWeight:700, color:'#2E7DB8', background:'none', border:'none', cursor:'pointer', padding:0 }}>
            ⚡ Generate
          </button>
        </div>
        <div style={{ position:'relative' }}>
          <input type={showPwd ? 'text' : 'password'} placeholder="Min 8 characters" value={form.password}
            onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            style={{ ...IS, paddingRight: 40 }} />
          <button type="button" onClick={() => setShowPwd(s => !s)}
            style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#9E9E9E' }}>
            {showPwd ? '🙈' : '👁️'}
          </button>
        </div>
        {form.password && form.password.length < 8 && (
          <div style={{ fontSize:10, color:'#E53935', marginTop:3 }}>Too short — minimum 8 characters</div>
        )}
      </div>
      <div style={{ background:'#E3F2FD', borderRadius:8, padding:'9px 12px', fontSize:10, color:'#1565C0', lineHeight:1.6 }}>
        ℹ️ The parent will log in at <strong>localhost:3000</strong> using these credentials.
        They can only view — not edit — {firstName}'s progress.
      </div>
      <button type="submit" disabled={saving} style={{
        padding: '11px', borderRadius: 9, fontSize: 13, fontWeight: 800,
        background: saving ? '#90A4AE' : 'linear-gradient(135deg, #1A4A6E 0%, #2E7DB8 100%)',
        color: 'white', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
      }}>
        {saving ? 'Creating Account…' : '✅ Create Parent Account'}
      </button>
    </form>
  )
}

const IS = {
  width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 12,
  border: '1.5px solid #E4EBF2', outline: 'none', boxSizing: 'border-box',
  background: '#FAFAFA', color: '#1A1A2E',
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function ParentAccessModal({ child, onClose }) {
  const { getAuthHeader } = useAuth()
  const [parents,  setParents]  = useState([])
  const [loading,  setLoading]  = useState(true)
  const [loadError, setLoadError] = useState('')  // ← NEW: track load failures
  const [toast,    setToast]    = useState(null)
  const [showForm, setShowForm] = useState(false)

  const showToast = useCallback((message, type = 'info') => setToast({ message, type }), [])

  const loadParents = useCallback(async () => {
    setLoading(true)
    setLoadError('')   // clear previous error on each attempt
    try {
      const r = await fetch(`/api/children/${child._id}/parent`, { headers: getAuthHeader() })
      const d = await r.json()

      if (d.success) {
        // Backend returns either d.parents (array) or d.parent (single object or null)
        // Normalise both shapes into an array
        let list
        if (Array.isArray(d.parents)) {
          list = d.parents
        } else if (d.parent) {
          list = [d.parent]   // ← single-object shape from current backend
        } else {
          list = []
        }
        setParents(list)
      } else {
        throw new Error(d.message || 'Server returned success:false')
      }
    } catch (err) {
      console.error('[ParentAccessModal] loadParents failed:', err)
      setLoadError(err.message || 'Failed to load parent accounts.')
      // Don't show toast on initial load — the inline error panel is enough
    } finally {
      setLoading(false)
    }
  }, [child._id, getAuthHeader])

  useEffect(() => { loadParents() }, [loadParents])

  // Auto-open form only when loaded successfully AND list is empty
  useEffect(() => {
    if (!loading && !loadError) setShowForm(parents.length === 0)
  }, [loading, loadError, parents.length])

  // Close on Escape
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  // ── Called after successful parent creation ───────────────────────────────
  // Optimistically set the new parent directly (don't prepend to stale state),
  // then reload in background for full data (lastLogin, _id, etc.)
  function handleParentCreated(newParent) {
    // Replace list entirely with the new parent so we're never adding to
    // stale optimistic state from a previous partial load.
    setParents([{ ...newParent, _isNew: true }])
    setShowForm(false)
    // Background reload — will replace optimistic entry with DB-accurate data
    loadParents()
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 1000, backdropFilter: 'blur(2px)',
      }} />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)', zIndex: 1001,
        background: '#F4F6FB', borderRadius: 18,
        width: '100%', maxWidth: 520, maxHeight: '90vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1A4A6E 0%, #2E7DB8 100%)',
          padding: '20px 24px', flexShrink: 0,
        }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:18, fontWeight:900, color:'white' }}>
                🔑 Parent Access
              </div>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.65)', marginTop:2 }}>
                {child.name} · Manage login credentials for parents/guardians
              </div>
            </div>
            <button onClick={onClose} style={{
              width:32, height:32, borderRadius:'50%',
              background:'rgba(255,255,255,0.15)', border:'none',
              color:'white', fontSize:16, cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px 24px' }}>

          {/* ── Linked parents section ───────────────────────────────────── */}
          <div style={{ marginBottom: 20 }}>
            {/* Section header with manual refresh button */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:800, color:'#9E9E9E', textTransform:'uppercase', letterSpacing:0.8 }}>
                Linked Parent Accounts ({loading ? '…' : parents.length})
              </div>
              {/* Manual refresh — always visible so user can force a reload */}
              {!loading && (
                <button
                  onClick={loadParents}
                  title="Refresh list"
                  style={{
                    background: 'none', border: '1px solid #E4EBF2', borderRadius: 6,
                    padding: '3px 9px', fontSize: 10, fontWeight: 700, color: '#9E9E9E',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  }}
                >
                  ↺ Refresh
                </button>
              )}
            </div>

            {loading ? (
              <div style={{ textAlign:'center', padding:'24px 0', color:'#9E9E9E', fontSize:12 }}>
                <div style={{ width:22, height:22, border:'2.5px solid #2E7DB8', borderTop:'2.5px solid transparent', borderRadius:'50%', animation:'spin .7s linear infinite', margin:'0 auto 8px' }} />
                Loading…
                <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
              </div>

            ) : loadError ? (
              /* ── Load error panel with retry ── */
              <div style={{
                background: '#FFF3F3', border: '1.5px solid #FFCDD2', borderRadius: 12,
                padding: '16px 18px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#C62828', marginBottom: 4 }}>
                  Couldn't load parent accounts
                </div>
                <div style={{ fontSize: 11, color: '#C62828', marginBottom: 12, lineHeight: 1.6 }}>
                  {loadError}
                </div>
                <button
                  onClick={loadParents}
                  style={{
                    padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    background: '#C62828', color: 'white', border: 'none', cursor: 'pointer',
                  }}
                >
                  ↺ Try again
                </button>
              </div>

            ) : parents.length === 0 ? (
              <div style={{
                textAlign:'center', padding:'24px 16px', background:'white',
                borderRadius:12, border:'1.5px dashed #E4EBF2', color:'#9E9E9E',
              }}>
                <div style={{ fontSize:32, marginBottom:8 }}>👨‍👩‍👧</div>
                <div style={{ fontSize:12, fontWeight:600 }}>No parent accounts linked yet</div>
                <div style={{ fontSize:11, marginTop:4 }}>
                  Create one below to give parents access to view {child.name.split(' ')[0]}'s progress.
                </div>
              </div>

            ) : (
              <div>
                {parents.map(p => (
                  <ParentRow
                    key={p._id}
                    parent={p}
                    childId={child._id}
                    authHdr={getAuthHeader}
                    onRefresh={loadParents}
                    onToast={showToast}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ── Add parent form (collapsible when parents exist) ─────────── */}
          {/* Hide the form entirely while load is in-flight or errored */}
          {!loading && !loadError && (
            <div style={{
              background: 'white', borderRadius: 14,
              border: `1.5px solid ${showForm ? '#2E7DB8' : '#E4EBF2'}`,
              overflow: 'hidden',
            }}>
              <button
                onClick={() => setShowForm(s => !s)}
                style={{
                  width: '100%', padding: '13px 18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: showForm ? '#EBF4FB' : 'white',
                  border: 'none', cursor: 'pointer',
                }}
              >
                <span style={{ fontSize:12, fontWeight:800, color:'#1A4A6E' }}>
                  ➕ Add Parent / Guardian Account
                </span>
                <span style={{ fontSize:14, color:'#1A4A6E', fontWeight:900, transition:'transform .2s',
                               transform: showForm ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  ▾
                </span>
              </button>

              {showForm && (
                <div style={{ padding: '4px 18px 18px' }}>
                  <CreateParentForm
                    childId={child._id}
                    childName={child.name}
                    authHdr={getAuthHeader}
                    onCreated={handleParentCreated}
                    onToast={showToast}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </>
  )
}